import { Helmet } from 'react-helmet-async';

/**
 * Per-route SEO surface. Hits every checkbox crawlers (Google, Bing,
 * Brave, Yandex, DuckDuckGo, Kagi) read:
 *
 *   - <title> + meta description (canonical signal)
 *   - canonical URL (deduplication signal  -  avoids ?theme=… cannibalising)
 *   - robots: index, follow, max-snippet:-1, max-image-preview:large
 *   - Open Graph + Twitter (LinkedIn, Slack, X unfurls)
 *   - JSON-LD structured data (per route  -  Organization on every page,
 *     plus Product/FAQPage on landing, plus BreadcrumbList in deep pages)
 *
 * Pre-render is recommended for full crawl quality (Bing/Brave don't
 * execute JS); see public/robots.txt + scripts/prerender.mjs in the
 * roadmap. Until then, these tags arrive in the DOM after hydration  - 
 * Googlebot WRS (which renders) sees them; non-rendering crawlers see
 * the static fallback in index.html.
 */

const SITE = 'https://tender-api.com';
const TWITTER = '@tenderapi';
const OG_DEFAULT_IMAGE = SITE + '/og/default.png';

export function Meta({
  title,
  description,
  path = '/',
  ogImage = OG_DEFAULT_IMAGE,
  jsonLd = [],
  noindex = false,
}) {
  const fullTitle = title.includes('tender-api') ? title : `${title} · tender-api`;
  const url = SITE + path;
  const ld = [organizationLd(), ...jsonLd];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content="tender-api" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Crawler hints */}
      <meta name="theme-color" content="#0a0a0a" media="(prefers-color-scheme: dark)" />
      <meta name="theme-color" content="#f5f1e8" media="(prefers-color-scheme: light)" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />

      {ld.map((obj, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}

/* ------------------------------------------------------------------ */
/* Re-usable JSON-LD builders                                          */
/* ------------------------------------------------------------------ */

export function organizationLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'tender-api',
    url: SITE,
    logo: SITE + '/icon-512.png',
    sameAs: ['https://tender-api.com'],
    description:
      'Every public procurement notice  -  TED, FTS, SAM, SIMAP, ProZorro and more  -  normalised into one JSON feed with minute-level freshness.',
    parentOrganization: {
      '@type': 'Organization',
      name: 'OmniCode Theodor Rumnici',
      url: 'https://tender-api.com',
    },
  };
}

export function productLd({ name, description, offers }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    brand: { '@type': 'Brand', name: 'tender-api' },
    offers: offers.map((o) => ({
      '@type': 'Offer',
      url: SITE + '/pricing',
      priceCurrency: 'USD',
      price: o.price,
      name: o.name,
      eligibleQuantity: o.users
        ? { '@type': 'QuantitativeValue', value: o.users, unitText: 'seat' }
        : undefined,
      availability: 'https://schema.org/InStock',
      priceValidUntil: '2099-12-31',
    })),
  };
}

export function faqLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a },
    })),
  };
}

export function breadcrumbLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: SITE + it.path,
    })),
  };
}

export function softwareApiLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebAPI',
    name: 'tender-api API',
    description:
      'Global public-procurement REST + SSE API. Government portals normalised; minute-level freshness; verbatim upstream payload on /raw.',
    documentation: SITE + '/docs',
    provider: { '@type': 'Organization', name: 'tender-api' },
  };
}
